import { Badge, Box, Button, Flex, Tooltip } from '@radix-ui/themes'
import { useAtomValue } from 'jotai'
import { Link, useLocation } from 'react-router'
import { envDisplayConfig } from '#app/config/env-display.config.ts'
import { DexRadixSize } from '#app/config/ui.config.ts'
import {
  globalProjectAtom,
  type GlobalWorkspace,
  globalWorkspaceAtom,
} from '#app/data/states/global-state.ts'
import { useBreakpoints } from '#app/hooks/breakpoints.hook.ts'
import { ThemeSwitch } from '#app/routes/_api+/api.theme-switch.tsx'
import { type AppEnv, type SupportContact } from '#app/utils/env.server.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import { DexIconBreadcrumbArrow } from '../ui/icons/dex-icons.tsx'
import { Logo } from './logo.tsx'
import { SupportDialog } from './support-dialog.tsx'
import { UserDropdown } from './user-dropdown.tsx'
import { WorkspaceSelector } from './workspace-selector.tsx'

type NavigationProps = {
  appEnv: AppEnv
  userPrefsTheme: 'light' | 'dark' | undefined
  userWorkspaces: GlobalWorkspace[]
  supportContacts: SupportContact[]
  onOpenReleaseNotes?: () => void
}

export function Navigation({
  appEnv,
  userPrefsTheme,
  userWorkspaces,
  supportContacts,
  onOpenReleaseNotes,
}: NavigationProps) {
  const user = useOptionalUser()
  const workspace = useAtomValue(globalWorkspaceAtom)
  const project = useAtomValue(globalProjectAtom)
  const { is3xl } = useBreakpoints()
  const location = useLocation()

  // Hide workspace/project breadcrumbs in admin routes
  const isAdminRoute = location.pathname.startsWith('/admin')

  const envConfig = envDisplayConfig[appEnv] ?? envDisplayConfig.production

  return (
    <header
      className={`sticky top-0 z-50 w-screen border-b border-border ${envConfig.headerBg}`}
    >
      <div className="mx-auto w-full px-6 py-1">
        <nav className="flex items-center justify-between gap-4">
          <Flex gap="4" align="center">
            {envConfig.badgeVisible ? (
              <Badge color="gray" variant="solid" highContrast>
                {envConfig.label}
              </Badge>
            ) : null}
            <Logo />
            {!isAdminRoute && userWorkspaces.length > 0 && (
              <Flex align="center" className="gap-0.5">
                <WorkspaceSelector workspaces={userWorkspaces} />
                <DexIconBreadcrumbArrow className="text-amber-600" />
                {user && workspace && (
                  <Link to={`/workspaces/${workspace.slug}`}>
                    <Button
                      size={is3xl ? DexRadixSize.DEFAULT : DexRadixSize.SMALL}
                      variant="soft"
                    >
                      Projects
                    </Button>
                  </Link>
                )}
                {project && workspace && (
                  <>
                    <DexIconBreadcrumbArrow className="text-amber-600" />

                    <Link
                      to={`/workspaces/${workspace.slug}/projects/${project.id}`}
                    >
                      <Button
                        size={is3xl ? DexRadixSize.DEFAULT : DexRadixSize.SMALL}
                        variant="surface"
                      >
                        <Tooltip
                          content={project.name}
                          side="bottom"
                          align="center"
                          delayDuration={1500}
                        >
                          <Box className="w-full max-w-[256px] overflow-hidden text-ellipsis whitespace-nowrap font-semibold">
                            {project.name}
                          </Box>
                        </Tooltip>
                      </Button>
                    </Link>
                  </>
                )}
              </Flex>
            )}
          </Flex>
          <Flex gap="1">
            <ThemeSwitch userPreference={userPrefsTheme} />
            <SupportDialog contacts={supportContacts} />
            {user ? (
              <UserDropdown
                onOpenReleaseNotes={onOpenReleaseNotes}
                selfHosted={appEnv === 'self-hosted'}
              />
            ) : (
              <Button
                size={is3xl ? DexRadixSize.DEFAULT : DexRadixSize.SMALL}
                asChild
              >
                <Link to="/login">
                  <span className="font-bold">Log In</span>
                </Link>
              </Button>
            )}
          </Flex>
        </nav>
      </div>
    </header>
  )
}
